/**
 * 小程序壳（mp-wrap）转发分享桥。
 *
 * H5 以 web-view 形式运行在小程序内时，微信浏览器 JSSDK 的分享 API 不可用；
 * 转发卡片内容由壳的 pages/webview/index 在 onMessage(bindmessage) 中接收 H5 的
 * postMessage 并合成（取最后一条分享类型消息，data.link 会被包装为
 * pages/webview/index?redirectUrl=<encodeURIComponent(link)>）。
 *
 * 契约（见 mp-wrap src/pages/webview/store.js onMessage / utils/common-util.js isShareType）：
 * wx.miniProgram.postMessage({ data: { type: 'onMenuShareAppMessage', data: { link } } })
 * type 必须是 onMenuShareAppMessage / onMenuShareQQ / onMenuShareQZone / onMenuShareTimeline 之一；
 * title/desc/imgUrl 省略时沿用壳远端 common-config 的默认分享配置。
 */
import routeConfigs from '@src/pages/app-router/route-configs';
import qs from './query-string-util';

/** postMessage 消息在壳侧被识别为分享配置的类型值 */
const SHARE_MESSAGE_TYPE = 'onMenuShareAppMessage';

/** 小程序转发来源的 entry_source 标识（区别于海报二维码 poster_qr 与复制链接 invite_link） */
const TEAM_SHARE_ENTRY_SOURCE = 'team_share';

/**
 * 当前 H5 是否运行在小程序 web-view 内。
 * 微信在 webview 中注入 __wxjs_environment = 'miniprogram'（JSSDK 标准环境标识）。
 *
 * @returns {boolean}
 */
export function isMiniprogramEnv() {
  return typeof window !== 'undefined' && window.__wxjs_environment === 'miniprogram';
}

/**
 * 向小程序壳推送一条转发分享 link。
 * 非小程序环境、jweixin 未加载或 postMessage 不可用时静默跳过（返回 false），
 * 保证普通浏览器访问零行为、webview 早退时壳侧沿用默认分享兜底。
 *
 * @param {string} link 分享落地 H5 地址（完整 URL，含 hash 路由与归因参数）
 * @returns {boolean} 是否成功发出
 */
export function postMiniprogramShareLink(link) {
  if (!isMiniprogramEnv() || typeof link !== 'string' || !link) {
    return false;
  }

  const miniProgram = window.wx && window.wx.miniProgram;
  if (!miniProgram || typeof miniProgram.postMessage !== 'function') {
    return false;
  }

  miniProgram.postMessage({
    data: {
      type: SHARE_MESSAGE_TYPE,
      data: { link }
    }
  });
  return true;
}

/**
 * 按分享人队伍事实构造转发落地 link（供持有 team_config 数据的页面复用）：
 * 有队伍 → Guest 客态页（携带队伍与个人分享码，接收者入队且分享人获得 is_inviter 归因）；
 * 无队伍 → 活动主入口（接收者按新老用户走组队流程）。
 * 构造与 ended-poster 的 Guest 链接约定一致：清 search（剥掉 webview 进入参数）、hash 路由。
 *
 * @param {object} context 分享人队伍事实。
 * @param {boolean} context.hasTeam 分享人当前是否拥有队伍。
 * @param {string} context.teamId 分享人队伍 ID（get_team 链路的 team_id）。
 * @param {string} [context.shareCode] 分享人个人分享码；缺省容忍（链接不带 share_code，归因缺失但可入队）。
 * @returns {string} 完整落地 URL；当前地址不可解析时返回空串。
 */
export function buildTeamShareUrl({ hasTeam, teamId, shareCode } = {}) {
  let url;
  try {
    url = new URL(window.location.href);
  } catch (error) {
    return '';
  }

  url.search = '';
  if (hasTeam && teamId) {
    url.hash =
      routeConfigs.GUEST.path +
      '?' +
      qs.stringify({
        entry_source: TEAM_SHARE_ENTRY_SOURCE,
        from_team_id: teamId,
        ...(shareCode ? { share_code: shareCode } : {})
      });
  } else {
    url.hash = routeConfigs.MAIN.path;
  }
  return url.href;
}

/**
 * 数据就绪页面统一入口：按队伍事实构造 link 并推送小程序壳（非小程序环境内部静默）。
 *
 * @param {object} context 同 buildTeamShareUrl。
 * @returns {boolean} 是否成功发出。
 */
export function postTeamShareContext(context) {
  return postMiniprogramShareLink(buildTeamShareUrl(context));
}
